import { EditorBookingItem } from '../editor-booking-item/editor-booking-item.model';


export class EditorBookingItemTree {
  state: string;
  bookingStatus: string;
  integrationInfo: { usage: any, description: { message: string, type: string }[] };
  bookingType: string;
  examType: string;
  risrAssessConnectionId: string;
  risrAssessBlueprintId: string;
  risrAssessItemSetGenerationMethod: string;
  risrAssessItemSetNumber: string;
  extraRoles: string[];
  itemList: EditorBookingItem[];

  constructor(obj?: any) {
    this.state = obj?.state ?? null;
    this.bookingStatus = obj?.bookingStatus ?? null;
    this.integrationInfo = obj?.integrationInfo ?? null;
    this.bookingType = obj?.bookingType ?? null;
    this.examType = obj?.examType ?? null;
    this.risrAssessConnectionId = obj?.risrAssessConnectionId ?? null;
    this.risrAssessBlueprintId = obj?.risrAssessBlueprintId ?? null;
    this.risrAssessItemSetGenerationMethod = obj?.risrAssessItemSetGenerationMethod ?? null;
    this.risrAssessItemSetNumber = obj?.risrAssessItemSetNumber ?? null;
    this.extraRoles = obj?.extraRoles ?? [];
    this.itemList = obj?.itemList ?? [];
  }

  createCopyWithoutItemList(): EditorBookingItemTree {
    return new EditorBookingItemTree({
      state: this.state,
      bookingStatus: this.bookingStatus,
      integrationInfo: this.integrationInfo,
      bookingType: this.bookingType,
      examType: this.examType,
      risrAssessConnectionId: this.risrAssessConnectionId,
      risrAssessBlueprintId: this.risrAssessBlueprintId,
      risrAssessItemSetGenerationMethod: this.risrAssessItemSetGenerationMethod,
      risrAssessItemSetNumber: this.risrAssessItemSetNumber,
      extraRoles: [...this.extraRoles]
    });
  }

  getBookingTypeName(bookingTypeNames: { id: string, name: string }[]): string {
    if (this.bookingType === null) { return ''; }
    return bookingTypeNames
      .find(bookingTypeName => bookingTypeName.id === this.bookingType)?.name ?? $localize `Not valid`;
  }

  getExamTypeName(examTypeNames: { id: string, name: string }[]): string {
    if (this.examType === null) { return ''; }
    return examTypeNames
      .find(examTypeName => examTypeName.id === this.examType)?.name ?? $localize `Not valid`;
  }

  getExtraRolesAsString(roleNames: { id: string, name: string }[]): string {
    if (this.extraRoles.length === 0) { return ''; }
    return this.extraRoles
      .map(extraRole =>
        roleNames.find(roleName => roleName.id === extraRole) ?? { id: extraRole, name: $localize `Not valid` }
      )
      .map(roleName => roleName.name)
      .join(', ');
  }

  getItemById(itemId: string): EditorBookingItem {
    return this.itemList.find(item => item.id === itemId);
  }

  getItemIndexById(itemId: string): number {
    return this.itemList.findIndex(item => item.id === itemId);
  }

  getFirstItemIndexByLevel(level: number): number {
    return this.itemList.findIndex(item => item.level === level);
  }

  getFirstItemIndexByAtMostLevel(level: number): number {
    return this.itemList.findIndex(item => item.level <= level);
  }

  getLastItemIndexByLevel(level: number): number {
    const itemIndex = [...this.itemList].reverse().findIndex(item => item.level === level);
    return itemIndex === - 1 ? -1 : this.itemList.length - itemIndex - 1;
  }

  getNextBranchItemIndex(itemIndex: number): number {
    const nextItemIndex = itemIndex + 1;
    const nextItemTree = new EditorBookingItemTree({ itemList: this.itemList.slice(nextItemIndex) });
    const nextBranchItemIndex = nextItemTree.getFirstItemIndexByAtMostLevel(this.itemList[itemIndex].level);
    return (nextBranchItemIndex === -1) ? this.itemList.length : nextItemIndex + nextBranchItemIndex;
  }

  getPreviousSiblingItemIndex(itemIndex: number): number {
    const firstSiblingIndex = this.getItemIndexById(this.itemList[itemIndex].parentId) + 1;
    const previousItemTree = new EditorBookingItemTree({ itemList: this.itemList.slice(firstSiblingIndex, itemIndex) });
    const previousSiblingRelativeItemIndex = previousItemTree.getLastItemIndexByLevel(this.itemList[itemIndex].level);
    return (previousSiblingRelativeItemIndex === -1) ? -1 : firstSiblingIndex + previousSiblingRelativeItemIndex;
  }

  getNextSiblingItemIndex(itemIndex: number): number {
    const nextItemIndex = itemIndex + 1;
    const nextParentBranchItemIndex = this.getNextBranchItemIndex(this.getItemIndexById(this.itemList[itemIndex].parentId));
    const nextItemTree = new EditorBookingItemTree({ itemList: this.itemList.slice(nextItemIndex, nextParentBranchItemIndex) });
    const nextSiblingRelativeItemIndex = nextItemTree.getFirstItemIndexByLevel(this.itemList[itemIndex].level);
    return (nextSiblingRelativeItemIndex === -1) ? -1 : nextItemIndex + nextSiblingRelativeItemIndex;
  }

  immutableItemListMoveUp(itemId: string): EditorBookingItemTree {
    const itemIndex = this.getItemIndexById(itemId);

    const firstPartStart = this.getPreviousSiblingItemIndex(itemIndex);

    if (firstPartStart === -1) {
      return this;
    }

    const secondPartStart = this.getNextBranchItemIndex(itemIndex);

    const newItemTree = Object.assign(new EditorBookingItemTree(), this, {
      itemList: [
        ...this.itemList.slice(0, firstPartStart),
        ...this.itemList.slice(itemIndex, secondPartStart),
        ...this.itemList.slice(firstPartStart, itemIndex),
        ...this.itemList.slice(secondPartStart)
      ]
    });

    const newItemIndex = firstPartStart;
    const item = newItemTree.itemList[newItemIndex];
    const newNextSiblingItemIndex = firstPartStart + (secondPartStart - itemIndex);
    const newNextSiblingItem = newItemTree.itemList[newNextSiblingItemIndex];

    if (item.isLastSibling) {
      newItemTree.itemList[newItemIndex] = Object.assign(
        new EditorBookingItem(),
        item,
        { isLastSibling: false }
      );
      newItemTree.itemList[newNextSiblingItemIndex] = Object.assign(
        new EditorBookingItem(),
        newNextSiblingItem,
        { isLastSibling: true }
      );
    }

    return newItemTree;
  }

  immutableItemListMoveDown(itemId: string): EditorBookingItemTree {
    const itemIndex = this.getItemIndexById(itemId);

    const firstPartStart = this.getNextSiblingItemIndex(itemIndex);

    if (firstPartStart === -1) {
      return this;
    }

    const secondPartStart = this.getNextBranchItemIndex(firstPartStart);

    const newItemTree = Object.assign(new EditorBookingItemTree(), this, {
      itemList: [
        ...this.itemList.slice(0, itemIndex),
        ...this.itemList.slice(firstPartStart, secondPartStart),
        ...this.itemList.slice(itemIndex, firstPartStart),
        ...this.itemList.slice(secondPartStart)
      ]
    });

    const newItemIndex = itemIndex + (secondPartStart - firstPartStart);
    const item = newItemTree.itemList[newItemIndex];
    const newPreviousSiblingItemIndex = itemIndex;
    const newPreviousSiblingItem = newItemTree.itemList[newPreviousSiblingItemIndex];

    if (newPreviousSiblingItem.isLastSibling) {
      newItemTree.itemList[newItemIndex] = Object.assign(
        new EditorBookingItem(),
        item,
        { isLastSibling: true }
      );
      newItemTree.itemList[newPreviousSiblingItemIndex] = Object.assign(
        new EditorBookingItem(),
        newPreviousSiblingItem,
        { isLastSibling: false }
      );
    }

    return newItemTree;
  }

  immutableItemListAddItemToParent(item: EditorBookingItem, parentItemId: string): EditorBookingItemTree {
    const parentItemIndex = this.getItemIndexById(parentItemId);
    const parentItem = this.itemList[parentItemIndex];

    const nextBranchItemIndex = this.getNextBranchItemIndex(parentItemIndex);

    const newItemTree = Object.assign(new EditorBookingItemTree(), this, {
      itemList: [
        ...this.itemList.slice(0, nextBranchItemIndex),
        item,
        ...this.itemList.slice(nextBranchItemIndex),
      ]
    });

    item.isLastSibling = true;
    if (parentItem.children.length) {
      const previousLastSiblingItemIndex = newItemTree.getItemIndexById(parentItem.children[parentItem.children.length - 1]);
      newItemTree.itemList[previousLastSiblingItemIndex] = Object.assign(
        new EditorBookingItem(),
        newItemTree.itemList[previousLastSiblingItemIndex],
        { isLastSibling: false }
      );
    }

    return newItemTree;
  }

  immutableItemListAddItemListAfter(itemList: EditorBookingItem[], siblingItemId: string): EditorBookingItemTree {
    const siblingItemIndex = this.getItemIndexById(siblingItemId);
    const nextBranchItemIndex = this.getNextBranchItemIndex(siblingItemIndex);

    const newItemTree = Object.assign(new EditorBookingItemTree(), this, {
      itemList: [
        ...this.itemList.slice(0, nextBranchItemIndex),
        ...itemList,
        ...this.itemList.slice(nextBranchItemIndex)
      ]
    });

    const newSiblingItemIndex = newItemTree.getItemIndexById(siblingItemId);
    const newSiblingItem = newItemTree.itemList[newSiblingItemIndex];

    itemList[0].isLastSibling = newSiblingItem.isLastSibling;
    if (newSiblingItem.isLastSibling) {
      newItemTree.itemList[newSiblingItemIndex] = Object.assign(
        new EditorBookingItem(),
        newSiblingItem,
        { isLastSibling: false }
      );
    }

    return newItemTree;
  }

  immutableItemListAddItemAfter(item: EditorBookingItem, siblingItemId: string): EditorBookingItemTree {
    return this.immutableItemListAddItemListAfter([item], siblingItemId);
  }

  immutableDeleteItem(itemId: string) {
    const itemIndex = this.getItemIndexById(itemId);
    const item = this.itemList[itemIndex];

    const nextBranchItemIndex = this.getNextBranchItemIndex(itemIndex);

    const previousSiblingItemIndex = this.getPreviousSiblingItemIndex(itemIndex);
    const previousSiblingItemId = previousSiblingItemIndex === -1 ? null : this.itemList[previousSiblingItemIndex].id;

    const newItemTree = Object.assign(new EditorBookingItemTree(), this, {
      itemList: [
        ...this.itemList.slice(0, itemIndex),
        ...this.itemList.slice(nextBranchItemIndex)
      ]
    });

    if (previousSiblingItemId !==  null) {
      const newPreviousSiblingItemIndex = newItemTree.getItemIndexById(previousSiblingItemId);

      if (item.isLastSibling) {
        newItemTree.itemList[newPreviousSiblingItemIndex] = Object.assign(
          new EditorBookingItem(),
          newItemTree.itemList[newPreviousSiblingItemIndex],
          { isLastSibling: true }
        );
      }
    }

    return newItemTree;
  }

  cloneBranch(itemId: string): EditorBookingItem[] {
    const itemIndex = this.getItemIndexById(itemId);

    const nextBranchItemIndex = this.getNextBranchItemIndex(itemIndex);

    const toCloneItemTree = new EditorBookingItemTree({ itemList: this.itemList.slice(itemIndex, nextBranchItemIndex) });
    const clonedItemTree = new EditorBookingItemTree({ itemList: [] });
    toCloneItemTree.itemList.forEach((toCloneItem, toCloneItemIndex) => {
      const clonedItem = toCloneItem.createClone();

      if (toCloneItemIndex > 0) {
        const toCloneItemParentItemIndex = toCloneItemTree.getItemIndexById(toCloneItem.parentId);
        clonedItem.parentId = clonedItemTree.itemList[toCloneItemParentItemIndex].id;
        clonedItemTree.itemList[toCloneItemParentItemIndex].children.push(clonedItem.id);
      }

      clonedItemTree.itemList.push(clonedItem);
    });

    clonedItemTree.itemList[0].name = `${clonedItemTree.itemList[0].name} (duplicate)`;

    clonedItemTree.itemList.forEach((clonedItem, clonedItemIndex) => {
      if (clonedItemIndex > 0) {
        const parentItemIndex = clonedItemTree.getItemIndexById(clonedItem.parentId);
        clonedItemTree.itemList[clonedItemIndex] =
          clonedItem.immutableSetSiblingsFromChildren(clonedItemTree.itemList[parentItemIndex].children);
      }
    });

    clonedItemTree.itemList.forEach((clonedItem, clonedItemIndex) => {
      if (clonedItemIndex > 0) {
        clonedItem.bundle = clonedItem.bundle.map(bundleItem => {
          const bundleItemIndex = toCloneItemTree.itemList.findIndex(toCloneItem => toCloneItem.id === bundleItem);
          return clonedItemTree.itemList[bundleItemIndex].id;
        });
      }
    });

    return clonedItemTree.itemList;
  }

  resetChildren(itemId: string) {
    const itemIndex = this.getItemIndexById(itemId);
    this.itemList[itemIndex] = Object.assign(
      new EditorBookingItem(),
      this.itemList[itemIndex],
      {
        children: this.itemList
          .filter(item => item.parentId === itemId)
          .map(item => item.id)
      }
    );
  }

  resetSiblingsForAllChildren(itemId: string) {
    const itemIndex = this.getItemIndexById(itemId);
    this.itemList[itemIndex].children.forEach(childItemId => {
      const childItemIndex = this.getItemIndexById(childItemId);
      this.itemList[childItemIndex] =
        this.itemList[childItemIndex].immutableSetSiblingsFromChildren(this.itemList[itemIndex].children);
    });
  }

  resetBundleForAllChildren(itemId: string) {
    const itemIndex = this.getItemIndexById(itemId);
    this.itemList[itemIndex].children.forEach(childItemId => {
      const childItemIndex = this.getItemIndexById(childItemId);
      this.itemList[childItemIndex] =
        this.itemList[childItemIndex].immutableRestrictBundleToSiblings(this.itemList[childItemIndex].siblings);
    });
  }
}
